import React, { createContext, useCallback, useContext, useState } from 'react';

export interface EvidenceItem {
  id: string;
  tool: string;
  label: string;
  data: any;
  timestamp: Date;
}

interface EvidencePanelState {
  isOpen: boolean;
  items: EvidenceItem[];
  activeIndex: number;
  openPanel: (item: EvidenceItem) => void;
  closePanel: () => void;
  setActiveIndex: (index: number) => void;
  resetPanel: () => void;
}

const EvidencePanelContext = createContext<EvidencePanelState | null>(null);

export const EvidencePanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<EvidenceItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const openPanel = useCallback((item: EvidenceItem) => {
    setItems((current) => {
      const exists = current.some((i) => i.id === item.id);
      if (exists) return current;
      return [...current, item];
    });
    setActiveIndex(() => {
      const existingIdx = items.findIndex((i) => i.id === item.id);
      if (existingIdx >= 0) return existingIdx;
      return items.length;
    });
    setIsOpen(true);
  }, [items]);

  const closePanel = useCallback(() => setIsOpen(false), []);

  const resetPanel = useCallback(() => {
    setIsOpen(false);
    setItems([]);
    setActiveIndex(0);
  }, []);

  return (
    <EvidencePanelContext.Provider
      value={{ isOpen, items, activeIndex, openPanel, closePanel, setActiveIndex, resetPanel }}
    >
      {children}
    </EvidencePanelContext.Provider>
  );
};

export const useEvidencePanel = (): EvidencePanelState => {
  const ctx = useContext(EvidencePanelContext);
  if (!ctx) throw new Error('useEvidencePanel must be used within EvidencePanelProvider');
  return ctx;
};
